import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, anonymousAPI, User, AUTH_TOKEN_KEY } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: (accessToken: string) => Promise<void>;
  signup: (email: string, password: string, fullName: string, state?: string | null, city?: string | null, language?: string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: { full_name?: string | null; state?: string | null; city?: string | null; language?: string }) => Promise<void>;
  updateProfilePhoto: (file: File) => Promise<void>;
  migrateAnonymousSession: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      authAPI
        .me()
        .then((res) => setUser(res.data))
        .catch((err) => {
          // Only remove token if it's actually invalid (401), not network errors
          if (err.response?.status === 401) {
            localStorage.removeItem(AUTH_TOKEN_KEY);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const migrateAnonymousSession = async () => {
    const sessionId = sessionStorage.getItem('anonymous_session_id');
    if (!sessionId) return;

    try {
      // Token is already in localStorage from login, backend will extract user_id from it
      await anonymousAPI.migrate(sessionId);
      sessionStorage.removeItem('anonymous_session_id');
    } catch (err) {
      // If migration fails (e.g., session not completed/expired), clear it so we don't spam retries
      sessionStorage.removeItem('anonymous_session_id');
      if ((err as any)?.response?.status !== 400) {
        console.error('Failed to migrate anonymous session:', err);
      }
    }
  };

  const login = async (email: string, password: string) => {
    const campaignSource = localStorage.getItem('campaign_source') || undefined;
    const campaignSession = localStorage.getItem('tracking_session_id') || undefined;
    const res = await authAPI.login({ email, password, campaign_source: campaignSource, campaign_session_id: campaignSession });
    localStorage.setItem(AUTH_TOKEN_KEY, res.data.access_token);
    const userRes = await authAPI.me();
    setUser(userRes.data);

    // Migrate anonymous session if exists
    await migrateAnonymousSession();
  };

  const googleLogin = async (accessToken: string) => {
    const res = await authAPI.googleAuth({ access_token: accessToken });
    localStorage.setItem(AUTH_TOKEN_KEY, res.data.access_token);
    const userRes = await authAPI.me();
    setUser(userRes.data);

    // Migrate anonymous session if exists
    await migrateAnonymousSession();
  };

  const signup = async (email: string, password: string, fullName: string, state?: string | null, city?: string | null, language?: string) => {
    // Get signup source from localStorage (set by landing page CTA tracking)
    const signupSource = localStorage.getItem('signup_source') || undefined;
    const campaignSource = localStorage.getItem('campaign_source') || undefined;
    const campaignSession = localStorage.getItem('tracking_session_id') || undefined;
    // Get language from localStorage if not provided
    const userLanguage = language || localStorage.getItem('i18nextLng') || 'pt';

    await authAPI.signup({
      email,
      password,
      full_name: fullName,
      signup_source: signupSource,
      state: state || undefined,
      city: city || undefined,
      language: userLanguage,
      campaign_source: campaignSource,
      campaign_session_id: campaignSession,
    });

    // Clear the source after using it
    if (signupSource) {
      localStorage.removeItem('signup_source');
    }
    // Keep campaign source for attribution across uploads; do not clear.

    // Don't auto-login - user needs to verify email first
    // Migration will happen when they log in after email verification
  };

  const logout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(null);
  };

  const updateProfile = async (data: { full_name?: string | null; state?: string | null; city?: string | null }) => {
    const res = await authAPI.updateProfile(data);
    setUser(res.data);
  };

  const updateProfilePhoto = async (file: File) => {
    const res = await authAPI.uploadProfilePhoto(file);
    setUser(res.data);
  };

  const refreshUser = useCallback(async () => {
    const res = await authAPI.me();
    setUser(res.data);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      googleLogin,
      signup,
      logout,
      updateProfile,
      updateProfilePhoto,
      migrateAnonymousSession,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
